/**
 * Servicio para gestión de restaurantes (solo admin)
 */

import { apiClient } from './api';
import { db } from './firebase';
import { collection, query, where, getDocs, doc, getDoc, orderBy } from 'firebase/firestore';

export interface RestaurantApplication {
  applicationId: string;
  ownerUid: string;
  name: string;
  slogan?: string; // Slogan o tagline del restaurante (opcional, máximo 100 caracteres)
  email: string;
  phone: string;
  addressText: string;
  addressLat?: number;
  addressLng?: number;
  images: string[]; // URLs de las imágenes del restaurante (mínimo 1, máximo 3)
  status: 'pendiente' | 'aprobado' | 'rechazado';
  reviewedAt?: {
    seconds: number;
    nanoseconds: number;
  };
  reviewedByAdminId?: string;
  rejectionReason?: string;
  createdAt: {
    seconds: number;
    nanoseconds: number;
  };
  updatedAt: {
    seconds: number;
    nanoseconds: number;
  };
}

export interface ListPendingRestaurantsResponse {
  success: boolean;
  applications: RestaurantApplication[];
  count: number;
}

export interface ListRejectedRestaurantsResponse {
  success: boolean;
  applications: RestaurantApplication[];
  count: number;
}

export interface ApproveRestaurantResponse {
  success: boolean;
  message: string;
  restaurant: {
    restaurantId: string;
    applicationId: string;
    status: string;
    ownerUid: string;
  };
}

export interface RejectRestaurantResponse {
  success: boolean;
  message: string;
  application: {
    applicationId: string;
    status: string;
  };
}

export interface Restaurant {
  restaurantId: string;
  ownerUid: string;
  name: string;
  slogan?: string; // Slogan o tagline del restaurante (opcional, máximo 100 caracteres)
  email: string;
  phone: string;
  address: {
    text: string;
    lat?: number;
    lng?: number;
  };
  images: string[];
  status: 'activo' | 'inactivo' | 'suspendido';
  operationalStatus: 'abierto' | 'cerrado';
  delivery: {
    baseFee: number;
    minimumOrder?: number;
    coverageRadiusKm?: number;
    type: 'plataforma' | 'propio';
  };
  createdAt: {
    seconds: number;
    nanoseconds: number;
  };
  updatedAt: {
    seconds: number;
    nanoseconds: number;
  };
}

export interface ListRestaurantsResponse {
  success: boolean;
  restaurants: Restaurant[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/**
 * Listar solicitudes de restaurantes pendientes (solo admin)
 * Migrado a Firestore
 */
export async function listPendingRestaurants(): Promise<RestaurantApplication[]> {
  try {
    const q = query(
      collection(db, 'restaurant_applications'),
      where('status', '==', 'pendiente'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      applicationId: doc.id,
      ...doc.data()
    } as any)) as RestaurantApplication[];
  } catch (error) {
    console.error("Error listing pending restaurants:", error);
    return [];
  }
}

/**
 * Listar solicitudes de restaurantes rechazadas (solo admin)
 * Migrado a Firestore
 */
export async function listRejectedRestaurants(): Promise<RestaurantApplication[]> {
  try {
    const q = query(
      collection(db, 'restaurant_applications'),
      where('status', '==', 'rechazado'),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      applicationId: doc.id,
      ...doc.data()
    } as any)) as RestaurantApplication[];
  } catch (error) {
    console.error("Error listing rejected restaurants:", error);
    return [];
  }
}

/**
 * Aprobar restaurante (solo admin)
 * Mantiene llamada Backend
 */
export async function approveRestaurant(applicationId: string): Promise<ApproveRestaurantResponse> {
  const response = await apiClient.post<ApproveRestaurantResponse>('/approveRestaurant', {
    applicationId
  });
  return response.data;
}

/**
 * Rechazar restaurante (solo admin)
 * Mantiene llamada Backend
 */
export async function rejectRestaurant(applicationId: string): Promise<RejectRestaurantResponse> {
  const response = await apiClient.post<RejectRestaurantResponse>('/rejectRestaurant', {
    applicationId
  });
  return response.data;
}

/**
 * Listar todos los restaurantes aprobados (solo admin)
 * Migrado a Firestore
 */
export async function listRestaurants(): Promise<Restaurant[]> {
  try {
    const q = query(collection(db, 'restaurants'), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      restaurantId: doc.id,
      ...doc.data()
    } as any)) as Restaurant[];
  } catch (error) {
    console.error("Error listing restaurants:", error);
    return [];
  }
}

export interface SetRestaurantStatusResponse {
  success: boolean;
  message: string;
  restaurant: {
    restaurantId: string;
    status: 'activo' | 'inactivo' | 'suspendido';
    previousStatus?: 'activo' | 'inactivo' | 'suspendido';
  };
}

/**
 * Cambiar status administrativo de restaurante (solo admin)
 */
export async function setRestaurantStatus(
  restaurantId: string,
  status: 'activo' | 'inactivo' | 'suspendido'
): Promise<SetRestaurantStatusResponse> {
  const response = await apiClient.put<SetRestaurantStatusResponse>('/setRestaurantStatus', {
    restaurantId,
    status
  });
  return response.data;
}

/**
 * Obtener detalles de un restaurante específico (admin)
 * Migrado a Firestore
 */
export async function getRestaurant(restaurantId: string): Promise<Restaurant> {
  try {
    const docRef = doc(db, 'restaurants', restaurantId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { restaurantId: docSnap.id, ...docSnap.data() } as any as Restaurant;
    }
    throw new Error("Restaurant not found");
  } catch (error) {
    console.error("Error getting restaurant:", error);
    throw error;
  }
}
